import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock, BadgeCheck, Shield } from "lucide-react";
import { toast } from "react-hot-toast";
import { isAuthenticated } from "@/utils/fetchWithAuth";

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/80">
      {children}
    </span>
  );
}

export default function PartnerApplyPublicPage() {
  const navigate = useNavigate();

  const handleJoin = () => {
    if (!isAuthenticated()) {
      toast("Login or create an account to request access.", { icon: "🔑" });
      navigate("/auth/login");
      return;
    }
    navigate("/dashboard/partner");
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="space-y-8"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Pill>
              <Lock className="h-4 w-4" />
              Invite-only · Manual approval
            </Pill>
            <Pill>
              <BadgeCheck className="h-4 w-4" />
              Fixed 20% share
            </Pill>
            <Pill>
              <Shield className="h-4 w-4" />
              Paid on usage
            </Pill>
          </div>

          <div className="space-y-3">
            <h1 className="text-4xl font-semibold tracking-tight">Sift Partner Program</h1>
            <p className="text-white/75 text-lg leading-relaxed">
              Partners earn a fixed share of Sift’s transaction fee by introducing organizations that run real volume
              through the platform.
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3">
            <div className="text-sm text-white/60">Program definition</div>
            <div className="text-white/85 leading-relaxed">
              Sift charges a fixed 10% transaction fee when payments move through the platform.
              Approved partners earn <span className="text-white font-semibold">20% of that fee</span>.
              This is not an affiliate program, partners are trusted introductions to decision-makers.
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-white font-semibold">Request access</div>
              <div className="text-white/70 mt-1">
                Login is required to submit a request and receive your referral code.
              </div>
            </div>

            <button
              onClick={handleJoin}
              className="inline-flex items-center gap-2 rounded-xl bg-white text-black px-5 py-2 text-sm font-semibold hover:bg-white/90 transition"
            >
              Request Partner Access <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="text-sm text-white/50">
            Want the details first?{" "}
            <Link to="/partner" className="text-white underline underline-offset-4">
              View the overview
            </Link>
            .
          </div>

          <div className="mt-6 text-xs text-white/40">
            © {new Date().getFullYear()} Sift Careers. All rights reserved.
          </div>
        </motion.div>
      </div>
    </div>
  );
}
